import React, { useState, useRef, useCallback, FormEvent, useEffect } from 'react';

import { Errors } from '../../components/Login/Errors/Errors';
import { ButtonRow } from '../../components/Login/ButtonRow/ButtonRow';
import { Button, BUTTON_TYPE_BLUE, BUTTON_TYPE_WHITE } from '../../components/Buttons/Button';
import { Text } from '../../components/Login/Text/Text';
import { CodeInput } from '../../components/Inputs/CodeInput';
import { TextInput } from '../../components/Inputs/TextInput';
import { Timer } from '../../components/Timer';
import Strings from '../../Strings';

// 2FA methods supported
export enum TwoFactorMethod {
  Totp = 'totp',
  Sms = 'sms',
  Email = 'email',
}

interface TwoFAFormProps {
  expiresAt?: string | number;
  isLoading: boolean;
  onBack: () => void;
  onFormSubmit: (data: { code: string; isRecoveryCode: boolean }) => void;
  onResend?: () => void;
  error?: string;
  twoFactorMethod?: TwoFactorMethod;
  phoneHint?: string;
  emailHint?: string;
  verifyAccount?: boolean;
}

export const TwoFAForm: React.FC<TwoFAFormProps> = ({
  expiresAt,
  isLoading,
  onBack,
  onFormSubmit,
  onResend,
  error,
  twoFactorMethod = TwoFactorMethod.Totp,
  phoneHint,
  emailHint,
  verifyAccount = false,
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [isCodeEmpty, setIsCodeEmpty] = useState(true);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Using any for ref since CodeInput is a JS class component
  const codeInputRef = useRef<any>(null);

  // Sync external error to internal state
  useEffect(() => {
    if (error) {
      setErrors([error]);
    } else {
      setErrors([]);
    }
  }, [error]);

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCodeChange = useCallback(() => {
    if (codeInputRef.current) {
      const length = codeInputRef.current.getValue().length;
      setIsCodeEmpty(length !== 6);
    }
  }, []);

  const handleRecoveryCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRecoveryCode(e.target.value);
  }, []);

  const handleFormSubmit = useCallback((event: FormEvent) => {
    event.preventDefault();
    setErrors([]);

    if (useRecoveryCode) {
      if (recoveryCode.trim().length === 0) {
        setErrors(['Please enter a recovery code']);
        return;
      }
      onFormSubmit({ code: recoveryCode.trim(), isRecoveryCode: true });
    } else {
      if (codeInputRef.current) {
        const code = codeInputRef.current.getValue();
        if (code.length !== 6) {
          setErrors(['Please enter the 6-digit code']);
          return;
        }
        onFormSubmit({ code, isRecoveryCode: false });
      }
    }
  }, [useRecoveryCode, recoveryCode, onFormSubmit]);

  const handleExpired = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleResend = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (onResend && resendCooldown === 0) {
      onResend();
      setResendCooldown(60); // 60 second cooldown
    }
  }, [onResend, resendCooldown]);

  const toggleRecoveryMode = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setUseRecoveryCode(prev => !prev);
    setErrors([]);
  }, []);

  // Get title based on 2FA method
  const getTitle = (): string => {
    switch (twoFactorMethod) {
      case TwoFactorMethod.Sms:
        return 'SMS Verification';
      case TwoFactorMethod.Email:
        return 'Email Verification';
      case TwoFactorMethod.Totp:
      default:
        return 'Two-Factor Authentication';
    }
  };

  // Get instructions based on 2FA method
  const getInstructions = (): string => {
    if (useRecoveryCode) {
      return 'Enter one of your recovery codes to sign in.';
    }
    
    switch (twoFactorMethod) {
      case TwoFactorMethod.Sms:
        return phoneHint 
          ? `Enter the 6-digit code sent to ${phoneHint}`
          : 'Enter the 6-digit code sent to your phone';
      case TwoFactorMethod.Email:
        return emailHint
          ? `Enter the 6-digit code sent to ${emailHint}`
          : 'Enter the 6-digit code sent to your email';
      case TwoFactorMethod.Totp:
      default:
        return 'Enter the 6-digit code from your authenticator app';
    }
  };

  // Determine if resend is available (not for TOTP)
  const canResend = twoFactorMethod !== TwoFactorMethod.Totp && onResend;

  // Determine if submit should be disabled
  const isSubmitDisabled = useRecoveryCode 
    ? recoveryCode.trim().length === 0 || isLoading
    : isCodeEmpty || isLoading;

  // Build text content
  let textContent;
  if (errors.length > 0) {
    textContent = <Errors NoMT errors={errors} />;
  } else {
    textContent = (
      <Text>
        {getInstructions()}
        {expiresAt && !useRecoveryCode && twoFactorMethod !== TwoFactorMethod.Totp ? (
          <>
            {' '}
            {Strings.twoFAFormCodeText?.replace('The one password code was sent to your mobile phone. ', '') || 'This code will expire in: '}
            <Timer expiresAt={expiresAt} onExpired={handleExpired} inlineMode />
          </>
        ) : null}
      </Text>
    );
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <div style={{ marginBottom: '10px', fontWeight: 500, fontSize: '16px' }}>
        {getTitle()}
      </div>
      
      <div>{textContent}</div>

      {useRecoveryCode ? (
        <TextInput
          name="recoveryCode"
          placeholder="Enter recovery code"
          value={recoveryCode}
          onChange={handleRecoveryCodeChange}
          align="left"
          autoComplete="off"
          data-testid="recovery-code"
        />
      ) : (
        <CodeInput ref={codeInputRef} onChange={handleCodeChange} />
      )}

      {/* Toggle between OTP and recovery code */}
      {!verifyAccount && (
          <div style={{ marginBottom: '15px', textAlign: 'center' }}>
          <Button type="button" onClick={toggleRecoveryMode} disabled={isLoading}>
            {useRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code instead'}
          </Button>
        </div>
      )}

      <ButtonRow
        left={
          canResend ? (
            <Button 
              type="button" 
              buttonType={BUTTON_TYPE_WHITE} 
              disabled={isLoading || resendCooldown > 0} 
              onClick={handleResend}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : Strings.resend}
            </Button>
          ) : (
            <Button type="button" buttonType={BUTTON_TYPE_WHITE} disabled={isLoading} onClick={onBack}>
              {Strings.back}
            </Button>
          )
        }
        right={
          <Button
            buttonType={BUTTON_TYPE_BLUE}
            type="submit"
            data-testid="submit"
            disabled={isSubmitDisabled}
          >
            {Strings.next}
          </Button>
        }
      />
    </form>
  );
};

export default TwoFAForm;
